/**
 * OperationsRoutesPage - FM Route Summary Dashboard
 * ==================================================
 * Shows contractor routes, attendance, and grouping opportunities.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function OperationsRoutesPage() {
  const [loading, setLoading] = useState(true);
  const [routeData, setRouteData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [expandedContractor, setExpandedContractor] = useState(null);

  const loadRoutes = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/api/operations/routes/summary?date=${selectedDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.ok) {
        setRouteData(res.data);
      } else {
        toast.error("Failed to load route data");
      }
    } catch (err) {
      console.error("Load routes error:", err);
      toast.error(err.response?.data?.detail || "Failed to load routes");
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    loadRoutes();
  }, [loadRoutes]);

  const openInMaps = (job) => {
    const query = encodeURIComponent(`${job.site_name || job.park_name || ''}, ${job.postcode || ''}`);
    window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-10 w-10 text-teal-600 mx-auto mb-4" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="text-slate-600">Loading routes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Route Summary</h1>
            <p className="text-slate-500">Contractor routes and scheduling</p>
          </div>
          <div className="flex items-center gap-4">
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-4 py-2 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
            />
            <Button onClick={loadRoutes} variant="outline">Refresh</Button>
          </div>
        </div>

        {/* Summary KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-teal-600">{routeData?.total_contractors || 0}</div>
              <p className="text-sm text-slate-500">Contractors Active</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-blue-600">{routeData?.total_jobs || 0}</div>
              <p className="text-sm text-slate-500">Jobs Scheduled</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-amber-600">
                {routeData?.grouping_opportunities?.total_same_site_jobs || 0}
              </div>
              <p className="text-sm text-slate-500">Same-Site Grouped</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-purple-600">
                {routeData?.grouping_opportunities?.total_nearby_pairs || 0}
              </div>
              <p className="text-sm text-slate-500">Nearby Opportunities</p>
            </CardContent>
          </Card>
        </div>

        {/* Contractor Routes */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-900">Contractor Routes</h2>
          
          {routeData?.routes?.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-slate-500">No contractors have jobs scheduled for this date</p>
              </CardContent>
            </Card>
          ) : (
            routeData?.routes?.map((route) => (
              <Card key={route.contractor_id} className="overflow-hidden">
                <CardHeader 
                  className="cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => setExpandedContractor(
                    expandedContractor === route.contractor_id ? null : route.contractor_id
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="bg-teal-100 text-teal-700 font-bold rounded-full w-10 h-10 flex items-center justify-center">
                        {route.contractor_name?.charAt(0) || '?'}
                      </div>
                      <div>
                        <CardTitle className="text-lg">{route.contractor_name || 'Unknown'}</CardTitle>
                        <p className="text-sm text-slate-500">
                          {route.total_jobs} jobs · {route.total_sites} sites
                          {route.estimated_distance_miles > 0 && ` · ~${route.estimated_distance_miles} miles`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        route.status === 'planned' ? 'bg-green-100 text-green-700' :
                        route.status === 'live' ? 'bg-blue-100 text-blue-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {route.status?.toUpperCase() || 'DRAFT'}
                      </span>
                      <svg 
                        className={`h-5 w-5 text-slate-400 transition-transform ${
                          expandedContractor === route.contractor_id ? 'rotate-180' : ''
                        }`}
                        fill="none" viewBox="0 0 24 24" stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </CardHeader>
                
                {expandedContractor === route.contractor_id && (
                  <CardContent className="border-t bg-slate-50">
                    <div className="space-y-3">
                      {route.ordered_jobs?.map((job, index) => (
                        <div 
                          key={job.id}
                          className="flex items-center justify-between bg-white rounded-xl p-4 border border-slate-200"
                        >
                          <div className="flex items-center gap-4">
                            <div className="bg-teal-600 text-white font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm">
                              {index + 1}
                            </div>
                            <div>
                              <p className="font-medium text-slate-800">
                                {job.site_name || job.park_name}
                              </p>
                              <p className="text-sm text-slate-500">
                                {job.asset_name || job.unit_name} · {job.postcode}
                              </p>
                              <p className="text-xs text-slate-400">
                                {job.job_reference} · {job.status}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              job.priority?.toLowerCase() === 'high' ? 'bg-red-100 text-red-700' :
                              job.priority?.toLowerCase() === 'urgent' ? 'bg-red-100 text-red-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {job.priority || 'Normal'}
                            </span>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openInMaps(job)}
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              </svg>
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            ))
          )}
        </div>

        {/* Grouping Opportunities */}
        {routeData?.grouping_opportunities && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-900">Grouping Opportunities</h2>
            
            {/* Same-Site Groups */}
            {Object.keys(routeData.grouping_opportunities.same_site_groups || {}).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Same-Site Groups</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {Object.values(routeData.grouping_opportunities.same_site_groups).map((group, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl p-3">
                      <div>
                        <p className="font-medium text-amber-800">{group.site_name}</p>
                        <p className="text-sm text-amber-600">{group.job_count} jobs on same site</p>
                      </div>
                      <span className="bg-amber-200 text-amber-800 px-3 py-1 rounded-full text-xs font-semibold">
                        {group.contractor_ids?.length || 1} contractor(s)
                      </span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Nearby Opportunities */}
            {routeData.grouping_opportunities.nearby_opportunities?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Nearby Jobs (within 10 miles)</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {routeData.grouping_opportunities.nearby_opportunities.slice(0, 10).map((pair, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-purple-50 border border-purple-200 rounded-xl p-3">
                      <div className="flex-1">
                        <p className="text-sm text-purple-800">
                          <span className="font-medium">{pair.job_a_site}</span>
                          {' → '}
                          <span className="font-medium">{pair.job_b_site}</span>
                        </p>
                        <p className="text-xs text-purple-600">
                          {pair.distance_miles} miles apart
                          {pair.same_contractor ? ' · Same contractor' : ' · Different contractors'}
                        </p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
